<script>
import { AuthLayout } from "@/components/layout";
import { authMethods, notificationMethods } from "@/state/helpers";
import { mapState, mapGetters } from "vuex";

import appConfig from "@/app.config";
import { required, email } from "vuelidate/lib/validators";

/**
 * Login component
 */
export default {
  page: {
    title: "Login",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: { AuthLayout },
  mounted() {
    if (process.env.NODE_ENV !== "production") {
      this.email = "stefan.quy+admin@gmail.com";
      this.password = "1234567";
    }
  },
  data() {
    return {
      email: "",
      password: "",
      submitted: false,
      authError: null,
      tryingToLogIn: false,
      isAuthError: false,
      rememberMe: false,
    };
  },
  validations: {
    email: { required, email },
    password: { required },
  },
  computed: {
    ...mapGetters("app", ["appVersion"]),
    notification() {
      return this.$store ? this.$store.state.notification : null;
    },
  },
  methods: {
    ...authMethods,
    ...notificationMethods,
    // Try to log the user in with the username
    // and password they provided.
    tryToLogIn() {
      this.submitted = true;
      // stop here if form is invalid
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      } else {
        this.tryingToLogIn = true;
        // Reset the authError if it existed.
        this.authError = null;
        return (
          this.logIn({
            email: this.email,
            password: this.password,
            rememberMe: this.rememberMe,
          })
            // eslint-disable-next-line no-unused-vars
            .then((token) => {
              this.tryingToLogIn = false;
              this.isAuthError = false;
              // Redirect to the originally requested page, or to the home page
              const redirectFrom = this.$route.query.redirectFrom;
              const isLogoutRedirect =
                redirectFrom === "/logout" ||
                redirectFrom?.startsWith("/logout?");
              if (redirectFrom === undefined || isLogoutRedirect) {
                this.$router.push({ name: "dashboard" });
              } else {
                this.$router.push(redirectFrom || { name: "default" });
              }
            })
            .catch((error) => {
              this.tryingToLogIn = false;
              this.authError = error ? error : "";
              this.isAuthError = true;
            })
        );
      }
    },
  },
};
</script>

<template>
  <AuthLayout>
    <div class="row justify-content-center">
      <div class="col-md-8 col-lg-6 col-xl-5">
        <div class="card overflow-hidden">
          <div class="bg-soft-primary">
            <div class="row">
              <div class="col-7">
                <div class="text-primary p-4">
                  <h5 class="text-primary">Welcome Back !</h5>
                  <p>Sign in to OMS bv Portal.</p>
                </div>
              </div>
              <div class="col-5 align-self-end">
                <img
                  src="@/assets/images/profile-img.png"
                  alt
                  class="img-fluid"
                />
              </div>
            </div>
          </div>
          <div class="card-body pt-0">
            <div>
              <router-link tag="a" to="/">
                <div class="avatar-md profile-user-wid mb-4">
                  <span
                    class="avatar-title rounded-circle bg-white border border-primary"
                  >
                    <img
                      src="@/assets/images/logo_oms.svg"
                      alt
                      width="50"
                      class="rounded"
                    />
                  </span>
                </div>
              </router-link>
            </div>
            <b-alert
              v-model="isAuthError"
              variant="danger"
              class="mt-3"
              dismissible
              >{{ authError }}</b-alert
            >

            <div
              v-if="notification.message"
              :class="'alert ' + notification.type"
            >
              {{ notification.message }}
            </div>

            <b-form class="p-2" @submit.prevent="tryToLogIn">
              <b-form-group
                id="input-group-1"
                label="Email"
                label-for="input-1"
              >
                <b-form-input
                  id="input-1"
                  v-model="email"
                  type="text"
                  placeholder="Enter email"
                  autocomplete="email"
                  :class="{ 'is-invalid': submitted && $v.email.$error }"
                ></b-form-input>
                <div
                  v-if="submitted && $v.email.$error"
                  class="invalid-feedback"
                >
                  <span v-if="!$v.email.required">Email is required.</span>
                  <span v-if="!$v.email.email">Please enter valid email.</span>
                </div>
              </b-form-group>

              <b-form-group
                id="input-group-2"
                label="Password"
                label-for="input-2"
              >
                <b-form-input
                  id="input-2"
                  v-model="password"
                  type="password"
                  placeholder="Enter password"
                  autocomplete="current-password"
                  :class="{ 'is-invalid': submitted && $v.password.$error }"
                ></b-form-input>
                <div
                  v-if="submitted && !$v.password.required"
                  class="invalid-feedback"
                >
                  Password is required.
                </div>
              </b-form-group>
              <div class="custom-control custom-checkbox">
                <input
                  id="customControlInline"
                  v-model="rememberMe"
                  type="checkbox"
                  class="custom-control-input"
                />
                <label class="custom-control-label" for="customControlInline"
                  >Remember me</label
                >
              </div>
              <div class="mt-3">
                <b-button type="submit" variant="primary" class="btn-block"
                  >Log In</b-button
                >
              </div>
              <div class="mt-4 text-center">
                <router-link tag="a" to="/forgot-password" class="text-muted">
                  <i class="mdi mdi-lock mr-1"></i> Forgot your password?
                </router-link>
              </div>
            </b-form>
          </div>
          <!-- end card-body -->
        </div>
        <!-- end card -->

        <div class="mt-3 text-center">
          <p>
            © {{ new Date().getFullYear() }} OMS bv
            <small>v{{ appVersion }}</small>
          </p>
        </div>
      </div>
      <!-- end col -->
    </div>
    <!-- end row -->
  </AuthLayout>
</template>

<style lang="scss" module></style>
